import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Mail, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_TEXT = {
  verified: "Sending — your domain is verified.",
  pending: "Domain verification pending. Add the DNS records in Resend.",
  not_added: "Sender domain not added in Resend yet.",
  no_api_key: "No Resend API key configured.",
  error: "Couldn't reach Resend to check status.",
  unknown: "Status unknown.",
};

export const EmailHealthIndicator = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/email-health").then((r) => setData(r.data)).catch(() => setData({ domain_status: "error", verified: false })).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const healthy = data?.verified && data?.key_configured;
  const dot = loading ? "bg-slate-300" : healthy ? "bg-green-500" : "bg-red-500";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="email-health-indicator" aria-label="Email delivery status"
          className="flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 hover:bg-muted transition-colors">
          <span className="relative flex items-center justify-center">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-background ${dot} ${healthy ? "animate-none" : "animate-pulse"}`} />
          </span>
          <span className="hidden md:block text-xs font-medium">{loading ? "…" : healthy ? "Email OK" : "Email issue"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72" data-testid="email-health-popover">
        <div className="flex items-center gap-2 mb-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : healthy ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-red-600" />}
          <p className="font-semibold text-sm">Email delivery</p>
          <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" onClick={load} data-testid="email-health-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
        {data && (
          <div className="space-y-1.5 text-xs">
            <p className="text-muted-foreground">{STATUS_TEXT[data.domain_status] || STATUS_TEXT.unknown}</p>
            <div className="flex justify-between"><span className="text-muted-foreground">Sender</span><span className="font-medium truncate max-w-[160px]">{data.sender_email || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Domain</span><span className={`font-semibold capitalize ${data.verified ? "text-green-600" : "text-red-600"}`}>{data.domain_status?.replace("_", " ")}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">API key</span><span className="font-medium">{data.key_configured ? "Configured" : "Missing"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Activation gate</span><span className="font-medium">{data.verification_required ? "On" : "Off"}</span></div>
            {!healthy && (
              <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="block mt-2 text-primary underline">Verify your domain in Resend →</a>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
